import {
  Section,
  SidebarNode,
  useSidebarTreeStore,
} from '../../../../store/storeSidebarTree';
import { Namespace } from '../../../../types/namespace';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { Space } from '../../../../util/Types/Sidebar';
import { FluxObject } from '../AgentsSidebar/context';
import { isFluxbotFlow, isFormFlow } from '../utils';

interface SidebarItemsBySection {
  privateObjects?: FluxObject[];
  privateNamespaces?: Namespace[];
}

const flowNodeKind = (
  flow: Flows,
  parentName?: string,
): SidebarNode['kind'] => {
  if (isFluxbotFlow(flow, parentName)) return 'fluxbot';
  if (isFormFlow(flow)) return 'form';
  return 'agent';
};

const walkFolder = (
  folder: Folder,
  section: Section,
  spaceId: string | null,
  parentName: string | undefined,
  out: SidebarNode[],
): void => {
  out.push({
    id: folder.id,
    parentId: folder.parentId ?? null,
    kind: 'folder',
    section: folder.section ?? section,
    spaceId,
    rank: folder.rank ?? '',
    localVersion: 0,
    name: folder.name,
    payload: {},
  });
  (folder.flows ?? []).forEach(flow => {
    out.push({
      id: flow.id,
      parentId: folder.id,
      kind: flowNodeKind(flow, folder.name),
      section: folder.section ?? section,
      spaceId,
      rank: flow.rank ?? '',
      localVersion: 0,
      name: flow.name,
      payload: {},
    });
  });
  (folder.objects ?? []).forEach(obj => {
    out.push({
      id: obj.id,
      parentId: folder.id,
      kind: 'object',
      section: folder.section ?? section,
      spaceId,
      rank: (obj as { rank?: string }).rank ?? '',
      localVersion: 0,
      name: obj.name ?? '',
      payload: {},
    });
  });
  (folder.namespaces ?? []).forEach(ns => {
    out.push({
      id: ns.id,
      parentId: folder.id,
      kind: 'vector',
      section: folder.section ?? section,
      spaceId,
      rank: (ns as { rank?: string }).rank ?? '',
      localVersion: 0,
      name: ns.name ?? '',
      payload: {},
    });
  });
  (folder.folders ?? []).forEach(sub =>
    walkFolder(sub, section, spaceId, folder.name, out),
  );
};

const buildFolderSectionMap = (
  folders: Folder[],
  fallbackSection: Section,
  map: Map<string, Section> = new Map(),
): Map<string, Section> => {
  for (const folder of folders) {
    const section = folder.section ?? fallbackSection;
    map.set(folder.id, section);
    if (folder.folders?.length) {
      buildFolderSectionMap(folder.folders, section, map);
    }
  }
  return map;
};

const pushObjectNode = (
  obj: FluxObject,
  spaceId: string | null,
  sectionByFolderId: Map<string, Section>,
  out: SidebarNode[],
): void => {
  const parentId = obj.folderId ?? null;
  const section =
    parentId !== null ? sectionByFolderId.get(parentId) ?? 'data' : 'data';
  out.push({
    id: obj.id,
    parentId,
    kind: 'object',
    section,
    spaceId,
    rank: (obj as { rank?: string }).rank ?? '',
    localVersion: 0,
    name: obj.name ?? '',
    payload: {},
  });
};

const pushNamespaceNode = (
  ns: Namespace,
  spaceId: string | null,
  sectionByFolderId: Map<string, Section>,
  out: SidebarNode[],
): void => {
  const parentId = ns.folderId ?? null;
  const section =
    parentId !== null ? sectionByFolderId.get(parentId) ?? 'data' : 'data';
  out.push({
    id: ns.id,
    parentId,
    kind: 'vector',
    section,
    spaceId,
    rank: (ns as { rank?: string }).rank ?? '',
    localVersion: 0,
    name: ns.name ?? '',
    payload: {},
  });
};

export const flattenSidebarSnapshot = (
  privateFolders: Folder[],
  spaces: Space[],
  items: SidebarItemsBySection = {},
): SidebarNode[] => {
  const out: SidebarNode[] = [];
  const sectionByFolderId = new Map<string, Section>();
  buildFolderSectionMap(privateFolders, 'agents', sectionByFolderId);
  spaces.forEach(space =>
    buildFolderSectionMap(space.folder ?? [], 'agents', sectionByFolderId),
  );
  privateFolders.forEach(folder =>
    walkFolder(folder, folder.section ?? 'agents', null, undefined, out),
  );
  spaces.forEach(space =>
    (space.folder ?? []).forEach(folder =>
      walkFolder(folder, folder.section ?? 'agents', space.id, undefined, out),
    ),
  );
  (items.privateObjects ?? []).forEach(obj =>
    pushObjectNode(obj, null, sectionByFolderId, out),
  );
  (items.privateNamespaces ?? []).forEach(ns =>
    pushNamespaceNode(ns, null, sectionByFolderId, out),
  );
  spaces.forEach(space => {
    (space.objects ?? []).forEach(obj =>
      pushObjectNode(obj as FluxObject, space.id, sectionByFolderId, out),
    );
    (space.namespaces ?? []).forEach(ns =>
      pushNamespaceNode(ns as Namespace, space.id, sectionByFolderId, out),
    );
  });
  return out;
};

export const hydrateSidebarStore = (
  privateFolders: Folder[],
  spaces: Space[],
  items: SidebarItemsBySection = {},
): void => {
  const nodes = flattenSidebarSnapshot(privateFolders, spaces, items);
  useSidebarTreeStore.getState().hydrate(nodes);
};
